// V1 Variant - Extended onboarding with psychology-based pages
// Builds on baseline with additional pages for:
// - Emotional engagement (transformation goals, satisfaction ratings)
// - Social proof insights (feedback pages with statistics)
// - Commitment building (readiness checks, past experience)
// - Personalization feel (progress page, ready feedback)

// ============================================
// Import BASELINE pages (reused from baseline)
// ============================================
import BudgetRangePage from '../../baseline/onboarding/BudgetRangePage'
import ClutterLevelPage from '../../baseline/onboarding/ClutterLevelPage'
import ColorPreferencePage from '../../baseline/onboarding/ColorPreferencePage'
import CompletePage from '../../baseline/onboarding/CompletePage'
import ConditionalBudgetPage from '../../baseline/onboarding/ConditionalBudgetPage'
import ConditionalClutterPage from '../../baseline/onboarding/ConditionalClutterPage'
import HelpMethodPage from '../../baseline/onboarding/HelpMethodPage'
import HomeSizePage from '../../baseline/onboarding/HomeSizePage'
import MoodImpactInsightPage from '../../baseline/onboarding/MoodImpactInsightPage'
import MoodScalePage from '../../baseline/onboarding/MoodScalePage'
import PersonalityTypePage from '../../baseline/onboarding/PersonalityTypePage'
import PlantPreferencePage from '../../baseline/onboarding/PlantPreferencePage'
import PriorityPage from '../../baseline/onboarding/PriorityPage'
import SpaceVibePage from '../../baseline/onboarding/SpaceVibePage'
import StyleVibePage from '../../baseline/onboarding/StyleVibePage'

// ============================================
// Import V1-ONLY pages (new psychological pages)
// ============================================
import ChangeReadinessPage from './ChangeReadinessPage'
import CurrentSatisfactionPage from './CurrentSatisfactionPage'
import DreamSpaceFeedbackPage from './DreamSpaceFeedbackPage'
import InspirationSourcePage from './InspirationSourcePage'
import ObstaclesFeedbackPage from './ObstaclesFeedbackPage'
import PastExperiencePage from './PastExperiencePage'
import TimeCommitmentPage from './TimeCommitmentPage'
import TransformationGoalPage from './TransformationGoalPage'
import TransformationReadyFeedbackPage from './TransformationReadyFeedbackPage'

import navigateToNextOnboardingPageAction from '@actions/navigateToNextOnboardingPageAction'
import skipOnboardingLoader from '@app-studio-web/ui/loaders/skipOnboardingLoader'

import { Outlet } from 'react-router-dom'

/**
 * V1 Variant Routes - Extended onboarding with psychology-based pages
 *
 * Uses path: 'v1' so URLs are /onboarding/v1/page-name
 * This matches LingoChat's A/B test structure.
 */
const onboardingRoutes = [
  {
    path: 'v1',
    element: <Outlet />,
    loader: skipOnboardingLoader,
    children: [
      // ============================================
      // INTRO - Setting the mood
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <SpaceVibePage />,
        path: 'space-vibe',
      },
      // NEW: Emotional engagement
      {
        action: navigateToNextOnboardingPageAction,
        element: <TransformationGoalPage />,
        path: 'transformation-goal',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <CurrentSatisfactionPage />,
        path: 'current-satisfaction',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <DreamSpaceFeedbackPage />,
        path: 'dream-space-feedback',
      },

      // ============================================
      // PREFERENCES - Understanding their style
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <HelpMethodPage />,
        path: 'help-method',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <HomeSizePage />,
        path: 'home-size',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <PersonalityTypePage />,
        path: 'personality-type',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ClutterLevelPage />,
        path: 'clutter-level',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ConditionalClutterPage />,
        path: 'conditional-clutter',
      },

      // ============================================
      // STYLE - Digging deeper
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <StyleVibePage />,
        path: 'style-vibe',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ColorPreferencePage />,
        path: 'color-preference',
      },
      // NEW: Inspiration source
      {
        action: navigateToNextOnboardingPageAction,
        element: <InspirationSourcePage />,
        path: 'inspiration-source',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <PriorityPage />,
        path: 'priority',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <PlantPreferencePage />,
        path: 'plant-preference',
      },

      // ============================================
      // COMMITMENT - Building investment
      // ============================================
      // NEW: Time commitment
      {
        action: navigateToNextOnboardingPageAction,
        element: <TimeCommitmentPage />,
        path: 'time-commitment',
      },
      // NEW: Past experience
      {
        action: navigateToNextOnboardingPageAction,
        element: <PastExperiencePage />,
        path: 'past-experience',
      },
      // NEW: Obstacles feedback
      {
        action: navigateToNextOnboardingPageAction,
        element: <ObstaclesFeedbackPage />,
        path: 'obstacles-feedback',
      },

      // ============================================
      // BUDGET
      // ============================================
      {
        action: navigateToNextOnboardingPageAction,
        element: <BudgetRangePage />,
        path: 'budget-range',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ConditionalBudgetPage />,
        path: 'conditional-budget',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <MoodScalePage />,
        path: 'mood-scale',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <MoodImpactInsightPage />,
        path: 'mood-impact-insight',
      },

      // ============================================
      // FINAL - Commitment and readiness
      // ============================================
      // NEW: Change readiness
      {
        action: navigateToNextOnboardingPageAction,
        element: <ChangeReadinessPage />,
        path: 'change-readiness',
      },
      // NEW: Transformation ready feedback
      {
        action: navigateToNextOnboardingPageAction,
        element: <TransformationReadyFeedbackPage />,
        path: 'transformation-ready-feedback',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <CompletePage />,
        path: 'complete',
      },
    ],
  },
]

export default onboardingRoutes
